import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { recordLogin } from '../../lib/recordLogin';
import { useAuth } from '../../contexts/AuthContext';
import { useLoginFSM } from '../../hooks/useLoginFSM';
import { ArrowLeft, Loader2, Eye, EyeOff, Mail, Lock, AlertCircle, CheckCircle, X, Image as ImageIcon, Phone, MapPin, Linkedin, Facebook, Shield } from 'lucide-react';
import TwoFactorModal from '../../components/TwoFactorModal';
import InvitedUser2FASetupModal from '../../components/InvitedUser2FASetupModal';
import ForgotPasswordFlow from '../../components/ForgotPasswordFlow';
import AuthPlatformNotice from '../../components/AuthPlatformNotice';

export default function PerplexityALMCustomerLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, signOut } = useAuth();
  const from = (location.state as any)?.from?.pathname || '/workday-client/dashboard';
  const { auth, needs2FA, needsInvited2FASetup, login, checkAndTransition, on2FASuccess, on2FACancel } = useLoginFSM({
    userType: 'workday_services_client',
    onSignOut: signOut,
  });
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoLoading, setLogoLoading] = useState(true);
  const [formData, setFormData] = useState({
    email: (location.state as any)?.email || '',
    password: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const justSignedUp = (location.state as any)?.justSignedUp || false;
  const signupHas2FA = (location.state as any)?.has2FA || false;

  // ── ONLY navigation rule ──────────────────────────────────────────────────
  useEffect(() => {
    if (auth.state === 'authenticated' && auth.user) {
      navigate(from, { replace: true });
    }
  }, [auth.state, auth.user]);

  useEffect(() => {
    fetchLogo();
  }, []);

  const fetchLogo = async () => {
    try {
      setLogoLoading(true);

      // Use a more reliable default logo
      const defaultLogo = 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=80&fit=crop&auto=format';
      setLogoUrl(defaultLogo);

      try {
        const { data, error } = await supabase
          .from('settings')
          .select('value')
          .eq('key', 'logo_url')
          .single();

        if (error) {
          console.warn('Could not fetch logo from settings:', error.message);
          setLogoLoading(false);
          return;
        }

        if (data?.value) {
          // Test if the image loads before setting it
          const img = new Image();
          img.onload = () => {
            setLogoUrl(data.value);
            setLogoLoading(false);
          };
          img.onerror = () => {
            console.warn('Failed to load custom logo, using default');
            setLogoLoading(false);
          };
          img.src = data.value;
        } else {
          setLogoLoading(false);
        }
      } catch (fetchError) {
        console.warn('Network error fetching logo, using default:', fetchError);
        setLogoLoading(false);
      }
    } catch (err) {
      setLogoLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleGoogleLogin = async () => {
    if (!acceptTerms) return;
    try {
      setIsGoogleSubmitting(true);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/workday-client/dashboard?user_type=workday_services_client`,
          queryParams: { access_type: 'offline', prompt: 'consent' },
          scopes: 'email profile',
        }
      });
      if (error) throw error;
    } catch (err: any) {
      console.error('Error with Google login:', err);
      setIsGoogleSubmitting(false);
    }
  };

  // ── Login handler — single-flight, only triggers FSM transitions, never navigates ──
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!acceptTerms || submitting) return;
    setSubmitting(true);

    try {
      const { success, user, session } = await login(formData.email, formData.password);

      if (!success || !user) {
        recordLogin({
          user_id: '',
          user_type: 'workday_client',
          email: formData.email,
          success: false,
          failure_reason: auth.error || 'Unknown error',
        });
        return;
      }

      // Role guard — verify the signed-in user has a workday client profile
      const { data: clientProfile } = await supabase
        .from('workday_client_profiles')
        .select('id')
        .eq('id', user.id)
        .maybeSingle();

      if (!clientProfile) {
        await signOut();
        recordLogin({
          user_id: user.id,
          user_type: 'workday_client',
          email: formData.email,
          success: false,
          failure_reason: 'Not a registered Workday Services client',
        });
        return;
      }

      recordLogin({
        user_id: user.id,
        user_type: 'workday_client',
        email: formData.email,
        success: true,
        session_id: session?.access_token?.slice(-16) || '',
      });

      await checkAndTransition(user, session?.access_token || '');
    } finally {
      setSubmitting(false);
    }
  };


  const TermsModal = ({ isOpen, onClose, title, content }: { 
    isOpen: boolean; 
    onClose: () => void; 
    title: string; 
    content: React.ReactNode; 
  }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-xl max-w-4xl max-h-[80vh] overflow-hidden shadow-2xl">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-6 h-6 text-gray-500" />
            </button>
          </div>
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {content}
          </div>
          <div className="p-6 border-t border-gray-200">
            <button
              onClick={onClose}
              className="w-full bg-[#40E0D0] text-black font-semibold py-3 rounded-lg hover:bg-[#40E0D0]/90 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const termsContent = (
    <div className="prose prose-gray max-w-none text-gray-700">
      <h3 className="text-xl font-semibold mb-4">Terms of Service for Workday Services Customers</h3>
      
      <section className="mb-6">
        <h4 className="text-lg font-semibold mb-2">1. Service Agreement</h4>
        <p className="mb-4">
          By creating an account as a Workday services customer, you agree to use our platform to access Workday consulting services and manage your projects professionally.
        </p>
      </section>

      <section className="mb-6">
        <h4 className="text-lg font-semibold mb-2">2. Customer Responsibilities</h4>
        <ul className="list-disc pl-6 space-y-2">
          <li>Provide accurate project requirements and specifications</li>
          <li>Maintain professional communication with consultants</li>
          <li>Make timely payments for services rendered</li>
          <li>Protect confidential information shared by consultants</li>
        </ul>
      </section>

      <section className="mb-6">
        <h4 className="text-lg font-semibold mb-2">3. Platform Usage</h4>
        <p className="mb-4">
          You may use our platform to find qualified Workday consultants, manage projects, and track progress. Misuse of the platform may result in account suspension.
        </p>
      </section>

      <section className="mb-6">
        <h4 className="text-lg font-semibold mb-2">4. Payment Terms</h4>
        <p className="mb-4">
          Payment terms are established between you and the consultant. Perplexity facilitates connections but is not responsible for payment disputes.
        </p>
      </section>
    </div>
  );

  const privacyContent = (
    <div className="prose prose-gray max-w-none text-gray-700">
      <h3 className="text-xl font-semibold mb-4">Privacy Policy for Workday Services Customers</h3>
      
      <section className="mb-6">
        <h4 className="text-lg font-semibold mb-2">1. Information We Collect</h4>
        <ul className="list-disc pl-6 space-y-2">
          <li>Company information (name, size, industry)</li>
          <li>Contact information (email, phone number)</li>
          <li>Project requirements and specifications</li>
          <li>Platform usage analytics</li>
        </ul>
      </section>

      <section className="mb-6">
        <h4 className="text-lg font-semibold mb-2">2. How We Use Your Information</h4>
        <ul className="list-disc pl-6 space-y-2">
          <li>Match you with qualified Workday consultants</li>
          <li>Facilitate communication and project management</li>
          <li>Process payments and manage contracts</li>
          <li>Improve our platform services</li>
        </ul>
      </section>

      <section className="mb-6">
        <h4 className="text-lg font-semibold mb-2">3. Data Protection</h4>
        <p className="mb-4">
          We implement industry-standard security measures to protect your company and project information. Your data is encrypted and stored securely.
        </p>
      </section>

      <section className="mb-6">
        <h4 className="text-lg font-semibold mb-2">4. Your Rights</h4>
        <p className="mb-4">
          You have the right to access, update, or delete your company information at any time through your account settings.
        </p>
      </section>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#40E0D0]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header with Perplexity Logo */}
      <header className="fixed w-full z-50 bg-black backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <div className="flex-1 flex items-center justify-between">
              <Link to="/" className="text-white hover:text-primary transition-colors -mt-4 -mb-4">
                {logoLoading ? (
                  <div className="h-32 md:h-28 w-[200px] flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                  </div>
                ) : logoUrl ? (
                  <>
                    <img 
                      src={logoUrl} 
                      className="h-32 md:h-28 w-auto object-contain max-w-[500px] md:max-w-[500px]"
                      onError={(e) => {
                        console.warn('Error loading logo image, falling back to text');
                        // Hide the image and show text fallback
                        e.currentTarget.style.display = 'none';
                        const textFallback = e.currentTarget.nextElementSibling as HTMLElement;
                        if (textFallback) {
                          textFallback.style.display = 'block';
                        }
                      }}
                    />
                    <div 
                      className="h-32 md:h-28 flex items-center justify-center text-primary text-2xl font-bold"
                      style={{ display: 'none' }}
                    >
                      PERPLEXITY
                    </div>
                  </>
                ) : (
                  <div className="h-32 md:h-28 w-[200px] flex items-center justify-center text-primary">
                    <ImageIcon size={32} />
                  </div>
                )}
              </Link>
              <button 
                onClick={() => navigate('/')}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Home
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-12 pt-32">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
            {!forgotPasswordMode && (
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Workday Services Customer Client Login</h1>
                <p className="text-gray-600">Access your Workday Services customer account</p>
              </div>
            )}

            {forgotPasswordMode ? (
              <ForgotPasswordFlow
                accentColor="#40E0D0"
                userType="client"
                onBack={() => { setForgotPasswordMode(false); }}
              />
            ) : (
              <>
                {justSignedUp && (
                  <div className="bg-green-50 border border-green-200 text-green-800 p-4 rounded-lg mb-6">
                    <div className="flex items-center mb-1">
                      <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0 text-green-600" />
                      <span className="text-sm font-semibold">Account created successfully!</span>
                    </div>
                    {signupHas2FA && (
                      <div className="flex items-start mt-2 ml-7">
                        <Shield className="w-4 h-4 mr-1.5 flex-shrink-0 text-green-600 mt-0.5" />
                        <span className="text-xs text-green-700">After signing in, a popup will ask for your Google Authenticator code. Have your app ready.</span>
                      </div>
                    )}
                  </div>
                )}

                {auth.state === 'error' && auth.error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg mb-6 flex items-center">
                    <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                    <span className="text-sm">{auth.error}</span>
                  </div>
                )}

                <form onSubmit={handleLogin} className="space-y-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent text-gray-900 bg-white"
                        placeholder="Enter your email"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        id="password"
                        name="password"
                        required
                        value={formData.password}
                        onChange={handleChange}
                        className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent text-gray-900 bg-white"
                        placeholder="Enter your password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        id="accept-terms"
                        type="checkbox"
                        checked={acceptTerms}
                        onChange={(e) => setAcceptTerms(e.target.checked)}
                        className="h-4 w-4 text-[#40E0D0] focus:ring-[#40E0D0] border-gray-300 rounded"
                      />
                      <label htmlFor="accept-terms" className="ml-2 block text-sm text-gray-700">
                        I accept the{' '}
                        <button
                          type="button"
                          onClick={() => setShowTermsModal(true)}
                          className="text-[#40E0D0] hover:text-[#40E0D0]/80 underline"
                        >
                          Terms of Service
                        </button>
                        {' '}and{' '}
                        <button
                          type="button"
                          onClick={() => setShowPrivacyModal(true)}
                          className="text-[#40E0D0] hover:text-[#40E0D0]/80 underline"
                        >
                          Privacy Policy
                        </button>
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting || auth.state === 'authenticating' || auth.state === 'verifying_2fa' || !acceptTerms}
                    className="w-full bg-[#40E0D0] text-black font-semibold py-3 rounded-lg hover:bg-[#40E0D0]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {submitting || auth.state === 'authenticating' || auth.state === 'verifying_2fa' ? (
                      <>
                        <Loader2 className="animate-spin mr-2 h-5 w-5" />
                        Signing In...
                      </>
                    ) : 'Sign In'}
                  </button>

                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => { setForgotPasswordMode(true); setError(''); }}
                      className="text-[#40E0D0] hover:text-[#40E0D0]/80 text-sm transition-colors"
                    >
                      Forgot your password?
                    </button>
                  </div>
                </form>

                <div className="mt-8 text-center">
                  <p className="text-gray-600">
                    Join the PerplexityATS network as a Workday Customer{' '}
                    <Link
                      to="/pro-client-signup"
                      className="text-[#40E0D0] hover:text-[#40E0D0]/80 font-medium transition-colors"
                    >
                      Sign up here
                    </Link>
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <AuthPlatformNotice />

      {/* Footer */}
      <footer className="bg-black py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <img src={logoUrl || 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=200&h=80&fit=crop&auto=format'} alt="Perplexity" className="h-12 w-auto mb-4" />
              <p className="text-gray-400 text-sm">
                A Registered Trademark of Perplexity Solved Solutions Inc
              </p>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Contact</h4>
              <div className="space-y-2">
                <a href="mailto:info@perplexityonline.com" 
                   className="text-gray-400 hover:text-white flex items-center group">
                  <Mail className="w-4 h-4 mr-2 text-primary group-hover:text-white transition-colors" />
                  info@perplexityonline.com
                </a>
                <a href="tel:18663078708" 
                   className="text-gray-400 hover:text-white flex items-center group">
                  <Phone className="w-4 h-4 mr-2 text-primary group-hover:text-white transition-colors" />
                  1 (866) 307-8708
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Address</h4>
              <div className="text-gray-400 flex items-start">
                <MapPin className="w-4 h-4 mr-2 mt-1 text-primary" />
                <p>
                  Headquarters<br />
                  5851 Legacy Cir<br />
                  Plano, Texas 75024
                </p>
              </div>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">Follow Us</h4>
              <div className="flex space-x-4">
                <a 
                  href="https://www.linkedin.com/company/perplexityhq/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-icon-linkedin"
                >
                  <div className="p-2 rounded-lg bg-gradient-to-br from-[#0077B5] to-[#00A0DC] hover:scale-110 transition-transform">
                    <Linkedin className="w-5 h-5 text-white" />
                  </div>
                </a>
                <a 
                  href="https://x.com/PerplexityHQ" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-icon-x"
                >
                  <div className="p-2 rounded-lg bg-gradient-to-br from-[#000000] to-[#333333] hover:scale-110 transition-transform">
                    <X className="w-5 h-5 text-white" />
                  </div>
                </a>
                <a 
                  href="https://www.facebook.com/Perplexitymeet/" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="social-icon-facebook"
                >
                  <div className="p-2 rounded-lg bg-gradient-to-br from-[#1877F2] to-[#00B2FF] hover:scale-110 transition-transform">
                    <Facebook className="w-5 h-5 text-white" />
                  </div>
                </a>
              </div>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-gray-800">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm">
                PERPLEXITY™, all rights reserved.
              </p>
              <div className="flex items-center space-x-6 mt-4 md:mt-0">
                <Link to="/terms" className="text-[#40E0D0] hover:text-[#0066FF] text-sm transition-colors">
                  Terms of Service
                </Link>
                <Link to="/privacy" className="text-[#40E0D0] hover:text-[#0066FF] text-sm transition-colors">
                  Privacy Statement
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Terms and Privacy Modals */}
      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        title="Terms of Service"
        content={termsContent}
      />

      <TermsModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        title="Privacy Policy"
        content={privacyContent}
      />

      {needsInvited2FASetup && auth.accessToken && (
        <InvitedUser2FASetupModal
          accessToken={auth.accessToken}
          onComplete={() => on2FASuccess(auth.user!)}
          onCancel={on2FACancel}
        />
      )}
      {needs2FA && auth.accessToken && (
        <TwoFactorModal
          onSuccess={() => on2FASuccess(auth.user!)}
          onCancel={on2FACancel}
          accessToken={auth.accessToken}
        />
      )}
    </div>
  );
}